export interface Step {
  name: string
  route: string
  isActive: boolean
  isComplete: boolean
  isValid: boolean
  id: string
}

export interface AuthStore {
  rawJwt: string | null
}

export interface ParsedJwt {
  [key: string]: string | null
}

export interface State {
  submissionAttemptCounter: number
  activeSubmission: Submission
  userIdentifier: string
  completed: boolean
  submissionFiles: SubmissionFilesGrouped
  errors: { [key: string]: VerifyResult }
  finalReviewVisited: boolean
  currentlySelectedEditorialOffices: string[]
  editorialOfficeSelectionMode: EditorialOfficeSelectionMode
  step: number
  das: DAS
  themedCollectionTitle: string
  themedCollectionResponse: string
  modalShownSinceExtraction: boolean
  hasExtractedDataFromFile: boolean
  extractedSnapshot?: {
    title?: boolean
    abstract?: boolean
    authors?: boolean
  }
  dataToCompare: DataToCompare
  featureFlagsEnabled: string[]
  editorialOffices: EditorialOffice[]
}

export interface DataToCompare {
  title: string
  abstract: string
  authors: Author[]
  ingestedAt: string | null
}
export interface LocalStorage {
  data: State
  expiry: string
}

export interface FeatureFlagsPayload {
  [key: string]: boolean
}

export type ArticleType = 'EdgeArticle' | 'ReviewArticle' | 'Perspective' | 'Comment'

export const ArticleTypeLabels: Record<ArticleType, string> = {
  EdgeArticle: 'Edge Article',
  ReviewArticle: 'Review Article',
  Perspective: 'Perspective',
  Comment: 'Comment',
}
export interface Country {
  name: string
  id: string
}

export interface EditorialOffice {
  peerReviewIdentifier: string
  name: string
  area: string
  affiliation: string
  image: URL
  profile: string
  enabled: boolean
  showImage?: boolean
}

export interface Institute {
  name: string
  city: string
  country?: Country
}

export interface Author {
  id: string
  firstName: string
  initials: string
  lastName: string
  isCorresponding: boolean
  email: string
  orcid: string
  affiliation: Institute
}

export enum ReviewerType {
  Recommend = 'Recommend',
  Oppose = 'Oppose',
}

export enum EditorialOfficeSelectionMode {
  RscDecide = 'RSC',
  AuthorDecided = 'Author',
}

export enum ReviewerAnonymisationType {
  Unknown = 'Unknown',
  SingleAnonymised = 'SingleAnonymised',
  DoubleAnonymised = 'DoubleAnonymised',
}

export interface ExtractedAffiliation {
  institution?: string
  city: string
  countryId: string
}

export interface ExtractedAuthor {
  firstName: string
  lastName: string
  isCorresponding: boolean
  email: string
  affiliations?: ExtractedAffiliation[]
}

export interface FileMetadata {
  fileId: string
  abstract: HtmlString
  title: HtmlString
  authors: ExtractedAuthor[]
}

export interface Reviewer {
  id: string
  firstName: string
  lastName: string
  email: string
  institution: string
  reason: string
  type: ReviewerType
}

export type HtmlString = string

export enum SubmissionFileState {
  Uploading = 'Uploading',
  Complete = 'Complete',
  Failure = 'Failure',
  FailureDuplicate = 'FailureDuplicate',
}

export enum SubmissionFileType {
  MainArticle = 'MainArticle',
  CoverLetter = 'CoverLetter',
  Esi = 'Esi',
  Figure = 'Figure',
  CrystalData = 'CrystalData',
  DAS = 'DAS',
  Unknown = 'Unknown',
}

export interface SubmissionFile {
  id: string
  file: string
  type: SubmissionFileType
  size: number
  state: SubmissionFileState
  fileData: File
}

export interface GroupedSubmissionFiles {
  type: SubmissionFileType
  files: SubmissionFile[]
}

export interface TitleAbstract {
  title: HtmlString
  abstract: HtmlString
}

export type Guid = string

export interface Submission extends TitleAbstract {
  articleType: ArticleType | undefined
  files: SubmissionFile[]

  authors: Author[]
  editorialOfficeIds: string[]
  reviewers: Reviewer[]
  termsAgreed: boolean
  submissionId: Guid
  ccdcNumbers: string
  peerReviewAnonymisation: ReviewerAnonymisationType
  transparentPeerReview: TransparentPeerReviewType
  userIdentifier: string
  themedCollectionTitle: string
  themedCollectionResponse: string
}
export interface SubmissionFilesGrouped {
  CoverLetter: SubmissionFile[]
  MainArticle: SubmissionFile[]
  DAS: SubmissionFile[]
  Esi: SubmissionFile[]
  CrystalData: SubmissionFile[]
  Figure: SubmissionFile[]
  Unknown: SubmissionFile[]
}

export enum TransparentPeerReviewType {
  Unknown = 'Unknown',
  Agree = 'Agree',
  Decline = 'Decline',
}

export interface VerifyResult {
  valid: boolean
  errors: string[]
  failedRules: { [key: string]: string }
  rules: string
  field: string
}

export enum DasOption {
  File = 'file',
  Custom = 'custom',
  Review = 'review',
}

export interface DAS {
  dasCustomSelected: boolean
  dasCustomContent: string
  dasCustomIsSent: boolean
  dasFileIsUploaded: boolean
  dasReview: boolean
  dasReviewIsSent: boolean
  selectedOption: DasOption | ''
}

export type FileSizeValidationInput = [FileList, string]

export interface TimeTracking {
  taskTimeSpent: string
  reviewTimeSpent: string
  pageTimesCompleted: number
}
export interface NewSubmissionParams {
  id: string
  articleType: ArticleType
}

export interface CompareFileIdParams {
  oldId: string
  newId: string
}

export interface UploadDasParams {
  submissionId: string
  content: string
  tempId: string
}

export type MutationCallback = (mutationsList: MutationRecord[]) => void

export interface TrackEventParams {
  event?: string
  category: string
  action: string
  label?: string
  value?: number
  noninteraction?: boolean
  [key: string]: unknown
}

export type MockHandler = (
  method: string,
  url?: string,
  data?: FormData,
  status?: number
) => Promise<unknown> | null

export type ShowToastType = {
  variant: string
  headingText: string
  bodyContent: string
  location: string
  isManual?: boolean
  isUpload?: boolean
  fileType?: string
}

export type ValidationKey =
  | 'uploadFilesVerifyResult'
  | 'articleInfoVerifyResult'
  | 'authorInfoVerifyResult'
  | 'reviewEditorialVerifyResult'
  | 'finalReviewVerifyResult'
  | 'dasFileVerifyResult'

export interface mockedSubmission {
  title: string
  abstract: string
  articleType: ArticleType
  files: SubmissionFile[]
  authors: Author[]
  editorialOfficeIds: string[]
  reviewers: Reviewer[]
  termsAgreed: boolean
  submissionId: Guid
  ccdcNumbers: string
  peerReviewAnonymisation: ReviewerAnonymisationType
  transparentPeerReview: TransparentPeerReviewType
  userIdentifier: string
  themedCollectionTitle: string
  themedCollectionResponse: string
}

export type GTMEventPayload =
  | { action: 'select', event: 'EditorSelected', editorId: string }
  | { action: 'remove', event: 'EditorRemoved', editorId: string }
  | { action: 'reorder', event: 'EditorsReordered', editors: string[] }
  | { action: 'change_mode', event: 'EditorialSelectionModeChanged', editorialOfficeSelectionMode: EditorialOfficeSelectionMode }

export type EditorialActionPayload =
  | EditorialOffice
  | string
  | EditorialOffice[]
  | EditorialOfficeSelectionMode
